package com.example.event_management_system.ui.components.calendar

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.ui.theme.Dimens
import com.example.event_management_system.ui.theme.statusColor
import com.example.event_management_system.util.currentLocalDate
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

@Composable
fun CalendarGrid(
    currentMonth: LocalDate,
    events: List<Event>,
    onEventClick: (Event) -> Unit,
    onDayClick: (LocalDate) -> Unit,
    modifier: Modifier = Modifier
) {
    val today = currentLocalDate()
    val daysOfWeek = listOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")
    val allDates = buildCalendarDates(currentMonth)
    val weeks = allDates.chunked(7)

    Column(modifier = modifier) {
        Row(modifier = Modifier.fillMaxWidth().padding(vertical = Dimens.spacingXSmall)) {
            daysOfWeek.forEach { day ->
                Text(
                    text = day,
                    modifier = Modifier.weight(1f),
                    textAlign = TextAlign.Center,
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        HorizontalDivider()

        weeks.forEachIndexed { weekIndex, week ->
            if (weekIndex > 0) HorizontalDivider()
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .heightIn(min = 90.dp)
            ) {
                week.forEachIndexed { dayIndex, (date, isCurrentMonth) ->
                    if (dayIndex > 0) VerticalDivider()
                    val dayEvents = events.filter { event ->
                        Instant.fromEpochMilliseconds(event.eventDate)
                            .toLocalDateTime(TimeZone.UTC).date == date
                    }
                    CalendarDayCell(
                        date = date,
                        isCurrentMonth = isCurrentMonth,
                        isToday = date == today,
                        events = dayEvents,
                        onClick = { onDayClick(date) },
                        onEventClick = onEventClick,
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight()
                    )
                }
            }
        }
    }
}

private fun buildCalendarDates(currentMonth: LocalDate): List<Pair<LocalDate, Boolean>> {
    val firstDayOfMonth = LocalDate(currentMonth.year, currentMonth.month, 1)
    val startOffset = (firstDayOfMonth.dayOfWeek.ordinal + 1) % 7

    val prevMonth = currentMonth.plus(-1, DateTimeUnit.MONTH)
    val daysInPrevMonth = daysInMonth(prevMonth)
    val daysInCurrentMonth = daysInMonth(currentMonth)
    val nextMonth = currentMonth.plus(1, DateTimeUnit.MONTH)

    val allDates = mutableListOf<Pair<LocalDate, Boolean>>()

    for (i in startOffset - 1 downTo 0) {
        val day = daysInPrevMonth - i
        allDates.add(LocalDate(prevMonth.year, prevMonth.month, day) to false)
    }
    for (day in 1..daysInCurrentMonth) {
        allDates.add(LocalDate(currentMonth.year, currentMonth.month, day) to true)
    }
    var nextDay = 1
    while (allDates.size < 35) {
        allDates.add(LocalDate(nextMonth.year, nextMonth.month, nextDay++) to false)
    }
    return allDates
}

private fun daysInMonth(month: LocalDate): Int = when (month.month) {
    kotlinx.datetime.Month.JANUARY, kotlinx.datetime.Month.MARCH, kotlinx.datetime.Month.MAY,
    kotlinx.datetime.Month.JULY, kotlinx.datetime.Month.AUGUST, kotlinx.datetime.Month.OCTOBER,
    kotlinx.datetime.Month.DECEMBER -> 31
    kotlinx.datetime.Month.APRIL, kotlinx.datetime.Month.JUNE, kotlinx.datetime.Month.SEPTEMBER,
    kotlinx.datetime.Month.NOVEMBER -> 30
    kotlinx.datetime.Month.FEBRUARY -> if ((month.year % 4 == 0 && month.year % 100 != 0) || month.year % 400 == 0) 29 else 28
}

@Composable
private fun CalendarDayCell(
    date: LocalDate,
    isCurrentMonth: Boolean,
    isToday: Boolean,
    events: List<Event>,
    onClick: () -> Unit,
    onEventClick: (Event) -> Unit,
    modifier: Modifier = Modifier
) {
    val dayLabel = when {
        !isCurrentMonth && date.dayOfMonth == 1 -> {
            val mon = date.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }
            "$mon 1"
        }
        else -> date.dayOfMonth.toString()
    }

    val sortedEvents = remember(events) { events.sortedBy { eventTimeSortKey(it.eventTime) } }
    val featured = sortedEvents.firstOrNull()
    val extraCount = (sortedEvents.size - 1).coerceAtLeast(0)
    var showPopup by remember { mutableStateOf(false) }

    Column(
        modifier = modifier
            .clickable {
                if (sortedEvents.isNotEmpty()) showPopup = true else onClick()
            }
            .padding(top = Dimens.spacingXxSmall, bottom = Dimens.spacingXSmall)
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopEnd
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                modifier = Modifier.padding(end = Dimens.spacingXSmall, top = Dimens.spacingXSmall)
            ) {
                if (extraCount > 0) {
                    Surface(
                        shape = RoundedCornerShape(50),
                        color = MaterialTheme.colorScheme.surfaceVariant,
                        shadowElevation = 0.dp,
                        modifier = Modifier.clickable { showPopup = true }
                    ) {
                        Text(
                            text = "+$extraCount more",
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.Medium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 1.dp)
                        )
                    }
                }
                if (isToday) {
                    Box(
                        modifier = Modifier
                            .size(Dimens.calendarDaySize)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = date.dayOfMonth.toString(),
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onPrimary
                        )
                    }
                } else {
                    Text(
                        text = dayLabel,
                        style = MaterialTheme.typography.labelMedium,
                        textAlign = TextAlign.End,
                        color = if (isCurrentMonth) MaterialTheme.colorScheme.onSurface
                                else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.35f)
                    )
                }
            }
        }

        Box(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .clipToBounds()
        ) {
            if (featured != null) {
                EventBar(
                    event = featured,
                    onClick = { onEventClick(featured) },
                    modifier = Modifier.height(36.dp)
                )
            }
            DropdownMenu(
                expanded = showPopup,
                onDismissRequest = { showPopup = false },
                modifier = Modifier.width(280.dp)
            ) {
                val dayName = date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
                val monthName = date.month.name.lowercase().replaceFirstChar { it.uppercase() }
                Column(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)) {
                    Text(
                        text = "$dayName, $monthName ${date.dayOfMonth}",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Spacer(Modifier.height(8.dp))
                    HorizontalDivider()
                    Spacer(Modifier.height(8.dp))
                    Column(
                        modifier = Modifier
                            .heightIn(max = 320.dp)
                            .verticalScroll(rememberScrollState())
                    ) {
                        sortedEvents.forEach { ev ->
                            EventBar(
                                event = ev,
                                onClick = {
                                    showPopup = false
                                    onEventClick(ev)
                                }
                            )
                            Spacer(Modifier.height(4.dp))
                        }
                    }
                }
            }
        }
    }
}

private fun eventTimeSortKey(raw: String): Int {
    val regex = Regex("^\\s*(\\d{1,2})(?::(\\d{2}))?\\s*(am|pm)?\\s*$", RegexOption.IGNORE_CASE)
    val m = regex.matchEntire(raw.trim()) ?: return Int.MAX_VALUE
    var h = m.groupValues[1].toIntOrNull() ?: return Int.MAX_VALUE
    val min = m.groupValues[2].toIntOrNull() ?: 0
    val period = m.groupValues[3].lowercase()
    if (period == "pm" && h < 12) h += 12
    if (period == "am" && h == 12) h = 0
    if (h !in 0..23 || min !in 0..59) return Int.MAX_VALUE
    return h * 60 + min
}

@Composable
private fun EventBar(
    event: Event,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val statusColor = event.status.statusColor
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 2.dp, vertical = 1.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(statusColor.copy(alpha = 0.25f))
            .clickable(onClick = onClick)
            .padding(horizontal = 4.dp, vertical = 2.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(6.dp)
                .clip(CircleShape)
                .background(statusColor)
        )
        Spacer(modifier = Modifier.width(4.dp))
        Column {
            Text(
                text = event.customerName,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = MaterialTheme.colorScheme.onSurface
            )
            val subtitle = buildString {
                if (event.eventTime.isNotBlank()) {
                    append(event.eventTime)
                    append(" · ")
                }
                append("${event.guestCount} guests")
            }
            Text(
                text = subtitle,
                style = MaterialTheme.typography.labelSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
